import React from 'react';

function Fingerprint(props) {
    const { useState, useEffect } = React;

    // Utility to generate a unique fingerprint
    const generateFingerprint = () => {
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2);
      return `${timestamp}-${random}`;
    };

    // Fallback function to get cookie using document.cookie
    const getCookieFallback = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };

    // Fallback function to set cookie using document.cookie
    const setCookieFallback = (name, value, days) => {
      const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Strict; Secure`;
    };

    // Fallback function to delete cookie using document.cookie
    const deleteCookieFallback = (name) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict; Secure`;
    };

    const FingerprintComponent = () => {
      const [fingerprint, setFingerprint] = useState('');
      const [cookieStatus, setCookieStatus] = useState('');
      const [error, setError] = useState('');

      useEffect(() => {
        let mounted = true; // Track if component is mounted

        const fetchFingerprint = async () => {
          try {
            let storedFingerprint;
            // let usedCookieStore = false;

            // Try using cookieStore first
            if (window.cookieStore) {
              try {
                const cookie = await window.cookieStore.get('machine_fingerprint');
                if (cookie) {
                  storedFingerprint = cookie.value;
                  // usedCookieStore = true;
                  if (mounted) {
                    setCookieStatus(`Existing fingerprint retrieved via cookieStore: ${storedFingerprint}`);
                  }
                }
              } catch (err) {
                if (mounted) {
                  setError(`cookieStore get failed: ${err.message}. Falling back to document.cookie`);
                }
              }
            } else if (mounted) {
              setError('cookieStore API not supported. Falling back to document.cookie');
            }

            // Fallback to document.cookie if cookieStore fails or is unavailable
            if (!storedFingerprint) {
              storedFingerprint = getCookieFallback('machine_fingerprint');
              if (storedFingerprint && mounted) {
                setCookieStatus(`Existing fingerprint retrieved via document.cookie: ${storedFingerprint}`);
              }
            }

            // If no fingerprint exists, generate and store one
            if (!storedFingerprint) {
              storedFingerprint = generateFingerprint();
              try {
                if (window.cookieStore) {
                  await window.cookieStore.set({
                    name: 'machine_fingerprint',
                    value: storedFingerprint,
                    expires: Date.now() + 365 * 24 * 60 * 60 * 1000, // 1 year
                    path: '/',
                    sameSite: 'Strict',
                  });
                  // usedCookieStore = true;
                  // Verify cookie was set
                  const verifyCookie = await window.cookieStore.get('machine_fingerprint');
                  if (!verifyCookie || verifyCookie.value !== storedFingerprint) {
                    throw new Error('Failed to verify cookie persistence after setting');
                  }
                  if (mounted) {
                    setCookieStatus(`New fingerprint generated and stored via cookieStore: ${storedFingerprint}`);
                  }
                } else {
                  throw new Error('cookieStore not available');
                }
              } catch (err) {
                if (mounted) {
                  setError(`cookieStore set failed: ${err.message}. Falling back to document.cookie`);
                }
                setCookieFallback('machine_fingerprint', storedFingerprint, 365);
                // Verify fallback cookie
                const verifyCookie = getCookieFallback('machine_fingerprint');
                if (verifyCookie !== storedFingerprint && mounted) {
                  setError('Failed to verify cookie persistence after fallback set');
                } else if (mounted) {
                  setCookieStatus(`New fingerprint generated and stored via document.cookie: ${storedFingerprint}`);
                }
              }
            }

            if (mounted) {
              setFingerprint(storedFingerprint);
            }
          } catch (err) {
            if (mounted) {
              setError(`Error accessing cookies: ${err.message}`);
            }
          }
        };

        fetchFingerprint();

        // Cleanup function to prevent state updates after unmount
        return () => {
          mounted = false;
        };
      }, []);

      // Function to clear fingerprint cookie
      const clearFingerprint = async () => {
        try {
          if (window.cookieStore) {
            await window.cookieStore.delete({ name: 'machine_fingerprint', path: '/' });
          } else {
            deleteCookieFallback('machine_fingerprint');
          }
          setFingerprint('');
          setCookieStatus('Fingerprint cleared');
          setError('');
        } catch (err) {
          setError(`Error clearing fingerprint: ${err.message}`);
        }
      };

      return (
        <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
          <h1 className="text-2xl font-bold mb-4 text-center text-gray-800">
            Machine Fingerprint
          </h1>
          <div className="mb-4">
            <p className="text-gray-600">
              <span className="font-semibold">Fingerprint ID:</span>{' '}
              {fingerprint || 'No fingerprint set'}
            </p>
            <p className="text-gray-600 mt-2">
              <span className="font-semibold">Status:</span> {cookieStatus}
            </p>
            {error && (
              <p className="text-red-500 mt-2">
                <span className="font-semibold">Error:</span> {error}
              </p>
            )}
          </div>
          <button
            onClick={clearFingerprint}
            className="w-full bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600 transition duration-200"
          >
            Clear Fingerprint
          </button>
        </div>
      );
    };

    return <FingerprintComponent />
}

export default Fingerprint;
